<ng-template #membersModal let-modal>
  <div class="modal-header">
    <div class="members-title">
      <span class="members-title-icon"><i class="fa fa-users"></i></span>
      <div>
        <h4 class="modal-title" id="account-members-title">Multiusuários</h4>
        <p class="members-subtitle">Pessoas da equipe com acesso a esta conta</p>
      </div>
    </div>
    <button type="button" class="btn-close" aria-label="Fechar" (click)="modal.dismiss()"
      data-testid="members-modal-close">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <div class="members-state" *ngIf="loading && !overview" data-testid="members-loading">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
      <p>Carregando a equipe...</p>
    </div>

    <div class="members-state" *ngIf="!loading && loadFailed && !overview" data-testid="members-error">
      <i class="fa fa-exclamation-circle fa-2x"></i>
      <p>Não foi possível carregar a equipe agora.</p>
      <button type="button" class="sf-btn sf-btn--secondary sf-btn--sm" (click)="load()"
        data-testid="members-retry">Tentar de novo</button>
    </div>

    <ng-container *ngIf="overview">
      <div class="members-summary">
        <div class="summary-card" data-testid="members-total">
          <span class="summary-label">Total de usuários</span>
          <span class="summary-value">{{ overview.totals.total }}</span>
          <span class="summary-hint">{{ overview.used }} de {{ overview.limit }} convites usados</span>
        </div>
        <div class="summary-card summary-card--active">
          <span class="summary-label">Ativos</span>
          <span class="summary-value">{{ overview.totals.active }}</span>
        </div>
        <div class="summary-card summary-card--pending">
          <span class="summary-label">Convites pendentes</span>
          <span class="summary-value">{{ overview.totals.pending }}</span>
        </div>

        <div class="summary-action">
          <!-- O tooltip fica no `span`: botão `disabled` não dispara os eventos de mouse. -->
          <span class="d-inline-block" tabindex="0" data-testid="members-invite-hint"
            [ngbTooltip]="limitReached ? 'Limite de ' + overview.limit + ' membros atingido. Remova alguém ou cancele um convite.' : ''">
            <button type="button" class="sf-btn sf-btn--primary" (click)="openInvite()" [disabled]="limitReached"
              data-testid="members-invite">
              <i class="fa fa-user-plus"></i>
              Convidar usuário
            </button>
          </span>
        </div>
      </div>

      <div class="members-table-wrapper">
        <table class="members-table">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Status</th>
              <th>Último acesso</th>
              <th class="actions-col">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let member of overview.members; trackBy: trackById" [attr.data-testid]="'member-' + member.id">
              <td>
                <div class="member-identity">
                  <span class="member-avatar" [class.owner]="member.is_owner">{{ initial(member) }}</span>
                  <div>
                    <span class="member-name">
                      {{ member.name }}
                      <span class="owner-tag" *ngIf="member.is_owner">Proprietário</span>
                    </span>
                    <span class="member-email">{{ member.email }}</span>
                  </div>
                </div>
              </td>
              <td>
                <span class="status-pill" [ngClass]="'status-pill--' + member.status">{{ statusLabel(member) }}</span>
              </td>
              <td class="access-col" [class.muted]="member.status !== 'active'">{{ accessLabel(member) }}</td>
              <td class="actions-col">
                <ng-container *ngIf="!member.is_owner">
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--icon sf-btn--sm"
                    *ngIf="member.status !== 'active'" ngbTooltip="Reenviar convite" (click)="resend(member)"
                    [disabled]="busyMemberIds.has(member.id)" [attr.data-testid]="'member-resend-' + member.id">
                    <i class="fa fa-refresh"></i>
                  </button>
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--icon sf-btn--sm btn-remove"
                    [ngbTooltip]="member.status === 'active' ? 'Remover da equipe' : 'Cancelar convite'"
                    (click)="confirmRemove(member)" [disabled]="busyMemberIds.has(member.id)"
                    [attr.data-testid]="'member-remove-' + member.id">
                    <i class="fa fa-trash-o"></i>
                  </button>
                </ng-container>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>
  </div>

  <div class="modal-footer">
    <p class="members-footnote">
      <i class="fa fa-shield"></i>
      Todos os membros têm acesso completo à conta, exceto cadastro, token da API e postback, que são do proprietário.
    </p>
    <button type="button" class="sf-btn sf-btn--secondary" (click)="modal.close()">Fechar</button>
  </div>
</ng-template>

<ng-template #inviteModal let-modal>
  <form [formGroup]="inviteForm" (ngSubmit)="submitInvite()" data-testid="invite-member-form">
    <div class="modal-header">
      <h4 class="modal-title" id="account-invite-title">
        <i class="fa fa-user-plus"></i>
        Convidar membro para a equipe
      </h4>
      <button type="button" class="btn-close" aria-label="Fechar" (click)="modal.dismiss()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      <div class="form-group">
        <label for="invite-name">Nome completo</label>
        <input id="invite-name" type="text" class="form-control" formControlName="name" placeholder="Ex.: Ana Silva"
          maxlength="100" data-testid="invite-member-name" />
        <small class="text-danger" *ngIf="inviteForm.get('name').touched && inviteForm.get('name').invalid">
          Informe o nome.
        </small>
      </div>

      <div class="form-group">
        <label for="invite-email">E-mail</label>
        <input id="invite-email" type="email" class="form-control" formControlName="email"
          placeholder="Ex.: ana@empresa.com" maxlength="45" data-testid="invite-member-email" />
        <small class="text-danger" *ngIf="inviteForm.get('email').touched && inviteForm.get('email').invalid">
          Informe um e-mail válido.
        </small>
      </div>

      <div class="invite-error" *ngIf="inviteError" role="alert" data-testid="invite-member-error">
        {{ inviteError }}
      </div>

      <p class="invite-hint">O convite será enviado ao e-mail informado e vale por 48 horas.</p>
    </div>

    <div class="modal-footer">
      <button type="button" class="sf-btn sf-btn--secondary" (click)="modal.dismiss()">Cancelar</button>
      <button type="submit" class="sf-btn sf-btn--primary" [appLoadingButton]="inviting"
        data-testid="invite-member-submit">
        Enviar convite
      </button>
    </div>
  </form>
</ng-template>
